import { FC } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Specification } from '../../types';
import { getOptionTitle, getOptionValue } from './wizard-utils';

const parseMultiValue = (value: string): string[] =>
  value
    ? value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

interface SpecFieldProps {
  spec: Specification;
  value: string;
  onChange: (value: string) => void;
  /**
   * When true, renders minimal select menus (no avatars or option descriptions).
   * Use inside table cells. Default false renders full rich menus.
   */
  compact?: boolean;
}

/**
 * Renders a single specification input (select, text, or number) for the
 * preview dialog. Shared by all question types that show spec inputs.
 * @param root0
 * @param root0.spec
 * @param root0.value
 * @param root0.onChange
 * @param root0.compact
 */
export const SpecField: FC<SpecFieldProps> = ({
  spec,
  value,
  onChange,
  compact = false,
}) => {
  const handleChange: (
    event: SelectChangeEvent<string>,
    child: React.ReactNode,
  ) => void = (event) => {
    event.stopPropagation();
    onChange(event.target.value);
  };

  if (spec.inputType === 'select' && spec.options) {
    // Compact mode (used in table cells) — keep the dropdown
    if (compact) {
      return (
        <FormControl
          fullWidth
          size="small"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <InputLabel>{spec.title}</InputLabel>
          <Select value={value} onChange={handleChange} label={spec.title}>
            {spec.options.map((option, index) => {
              const optValue = getOptionValue(option);
              const optTitle = getOptionTitle(option);
              return (
                <MenuItem key={`option-${index}`} value={optValue}>
                  {optTitle}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }

    // Full mode — chip buttons. Title → description → chips.
    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Typography variant="body2" fontWeight={500}>
          {spec.title}
        </Typography>
        {spec.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, mb: 1, display: 'block' }}
          >
            {spec.description}
          </Typography>
        )}
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: spec.description ? 0 : 1 }}
        >
          {spec.options.map((option, index) => {
            const optValue = getOptionValue(option);
            const optTitle = getOptionTitle(option);
            const isSelected = value === optValue;
            return (
              <Tooltip
                title={(option as Record<string, any>)?.description || ''}
                key={`option-${index}`}
              >
                <Chip
                  key={`option-${index}`}
                  label={optTitle}
                  onClick={(evt) => {
                    evt.stopPropagation();
                    onChange(optValue);
                  }}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    borderWidth: 2,
                    fontWeight: 500,
                    px: 1,
                    py: 2.5,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </Box>
    );
  }

  if (spec.inputType === 'multiselect' && spec.options) {
    const selectedValues = parseMultiValue(value);
    const selectedSet = new Set(selectedValues);

    const toggleValue = (optValue: string): void => {
      const next = selectedSet.has(optValue)
        ? selectedValues.filter((v) => v !== optValue)
        : [...selectedValues, optValue];
      onChange(next.join(','));
    };

    if (compact) {
      const optionLabels = new Map<string, string>();
      for (const opt of spec.options) {
        optionLabels.set(getOptionValue(opt), getOptionTitle(opt));
      }
      return (
        <FormControl
          fullWidth
          size="small"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <InputLabel>{spec.title}</InputLabel>
          <Select
            multiple
            value={selectedValues}
            onChange={(event) => {
              event.stopPropagation();
              const next = event.target.value;
              const arr = Array.isArray(next) ? next : [next];
              onChange(arr.join(','));
            }}
            label={spec.title}
            renderValue={(selected) =>
              (selected as string[])
                .map((v) => optionLabels.get(v) ?? v)
                .join(', ')
            }
          >
            {spec.options.map((option, index) => {
              const optValue = getOptionValue(option);
              const optTitle = getOptionTitle(option);
              return (
                <MenuItem key={`option-${index}`} value={optValue}>
                  <Checkbox checked={selectedSet.has(optValue)} size="small" />
                  <ListItemText primary={optTitle} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }

    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Typography variant="body2" fontWeight={500}>
          {spec.title}
        </Typography>
        {spec.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, mb: 1, display: 'block' }}
          >
            {spec.description}
          </Typography>
        )}
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: spec.description ? 0 : 1 }}
        >
          {spec.options.map((option, index) => {
            const optValue = getOptionValue(option);
            const optTitle = getOptionTitle(option);
            const isSelected = selectedSet.has(optValue);
            return (
              <Tooltip
                title={(option as Record<string, any>)?.description || ''}
                key={`option-${index}`}
              >
                <Chip
                  key={`option-${index}`}
                  label={optTitle}
                  onClick={(evt) => {
                    evt.stopPropagation();
                    toggleValue(optValue);
                  }}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    borderWidth: 2,
                    fontWeight: 500,
                    px: 1,
                    py: 2.5,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </Box>
    );
  }

  if (spec.inputType === 'text') {
    return (
      <TextField
        fullWidth
        size="small"
        label={spec.title}
        required={spec.required}
        helperText={!compact ? spec.description : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      />
    );
  }

  if (spec.inputType === 'number') {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label={spec.title}
        required={spec.required}
        helperText={!compact ? spec.description : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        InputProps={
          !compact && spec.unit
            ? {
                endAdornment: (
                  <Typography variant="caption" color="text.secondary">
                    {spec.unit}
                  </Typography>
                ),
              }
            : undefined
        }
      />
    );
  }

  return null;
};
